import { useCallback, useMemo } from 'react';
import { PLAYBACK_STATUS } from '../config/videoPlayer.config';

/**
 * Provides the user-facing playback commands for the video player.
 *
 * The hook owns what happens when the user plays, pauses, resumes, stops, restarts,
 * accepts the runtime gate, or clears and restarts the stream. Smaller hooks still own
 * the browser play attempt, stream setup, timers, and source lifecycle details.
 */
const useVideoPlaybackActions = ({
  acceptWatchTimeLimit,
  attachStream,
  cancelPendingRecovery,
  cancelPendingStartup,
  hasAttachedStream,
  isStillCurrentPlaybackRequest,
  muted,
  pauseWatchTimeLimitTimer,
  playbackStatusRef,
  playAfterVideoElementRemountsRef,
  releaseStream,
  restartStreamAfterPlaybackProblem,
  resetVideoPlayer,
  retryAttemptRef,
  setPlaybackStatus,
  src,
  startNewPlaybackRequest,
  startPlayingWhenEnoughVideoIsReady,
  startWatchTimeLimitTimer,
  startupDelayMs,
  tryToPlayVideo, // Function from useVideoPlayAttempt
  updateReadyVideoProgress,
  videoRef,
  volume,
  getIsWatchTimeLimitReached,
}) => {
  const play = useCallback(
    (delayOverrideMs = startupDelayMs) => {
      if (getIsWatchTimeLimitReached() || hasAttachedStream()) return;

      const video = videoRef.current;
      if (!video) return;
      const requestId = startNewPlaybackRequest(PLAYBACK_STATUS.starting);

      const didAttachStream = attachStream({
        video,
        src,
        requestId,
        muted,
        volume,
        onReady: (readyVideo, reason) => {
          startPlayingWhenEnoughVideoIsReady(readyVideo, requestId, reason, delayOverrideMs);
        },
        onFatalError: () => {
          restartStreamAfterPlaybackProblem({
            forceStreamCacheClear: true,
          });
        },
      });

      if (!didAttachStream) {
        console.error('HLS is not supported in this browser');
        setPlaybackStatus(PLAYBACK_STATUS.idle);
      }
    },
    [
      attachStream,
      hasAttachedStream,
      muted,
      restartStreamAfterPlaybackProblem,
      setPlaybackStatus,
      src,
      startNewPlaybackRequest,
      startPlayingWhenEnoughVideoIsReady,
      startupDelayMs,
      videoRef,
      volume,
      getIsWatchTimeLimitReached,
    ],
  );

  const pause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const requestId = startNewPlaybackRequest(PLAYBACK_STATUS.paused);

    cancelPendingStartup();
    cancelPendingRecovery();
    video.pause();
    pauseWatchTimeLimitTimer();

    if (isStillCurrentPlaybackRequest(requestId)) {
      setPlaybackStatus(PLAYBACK_STATUS.paused);
    }
  }, [
    cancelPendingRecovery,
    cancelPendingStartup,
    isStillCurrentPlaybackRequest,
    pauseWatchTimeLimitTimer,
    setPlaybackStatus,
    startNewPlaybackRequest,
    videoRef,
  ]);

  const resume = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;

    const requestId = startNewPlaybackRequest(PLAYBACK_STATUS.starting);

    cancelPendingRecovery();
    cancelPendingStartup();

    const playResult = await tryToPlayVideo(video, requestId, 'Resume playback');

    if (!isStillCurrentPlaybackRequest(requestId) || getIsWatchTimeLimitReached()) return;

    if (!playResult.didPlay) {
      if (!playResult.wasStale) {
        setPlaybackStatus(PLAYBACK_STATUS.paused);
      }

      return;
    }

    if (video.paused || video.ended) return;

    setPlaybackStatus(PLAYBACK_STATUS.playing);
    updateReadyVideoProgress();
    startWatchTimeLimitTimer();
  }, [
    cancelPendingRecovery,
    cancelPendingStartup,
    isStillCurrentPlaybackRequest,
    setPlaybackStatus,
    startNewPlaybackRequest,
    startWatchTimeLimitTimer,
    tryToPlayVideo,
    updateReadyVideoProgress,
    videoRef,
    getIsWatchTimeLimitReached,
  ]);

  const stop = useCallback(() => {
    retryAttemptRef.current = 0;
    cancelPendingRecovery();
    cancelPendingStartup();
    resetVideoPlayer({ nextStatus: PLAYBACK_STATUS.idle, resetWatchTime: true });
    releaseStream();
  }, [cancelPendingRecovery, cancelPendingStartup, releaseStream, resetVideoPlayer, retryAttemptRef]);

  const restart = useCallback(() => {
    retryAttemptRef.current = 0;
    cancelPendingRecovery();
    cancelPendingStartup();
    resetVideoPlayer({ nextStatus: PLAYBACK_STATUS.idle });
    play();
  }, [cancelPendingRecovery, cancelPendingStartup, play, resetVideoPlayer, retryAttemptRef]);

  const resetAndRestart = useCallback(() => {
    retryAttemptRef.current = 0;
    restartStreamAfterPlaybackProblem({
      allowRestartWhilePaused: true,
      forceStreamCacheClear: true,
    });
  }, [restartStreamAfterPlaybackProblem, retryAttemptRef]);

  const acceptRuntimeGate = useCallback(() => {
    const wasBlocked = getIsWatchTimeLimitReached();
    acceptWatchTimeLimit();

    if (wasBlocked) {
      cancelPendingRecovery();
      cancelPendingStartup();
      resetVideoPlayer({ nextStatus: PLAYBACK_STATUS.starting, remountMedia: true });
      playAfterVideoElementRemountsRef.current = startupDelayMs;
      return;
    }

    if (playbackStatusRef.current === PLAYBACK_STATUS.playing) {
      startWatchTimeLimitTimer();
    }
  }, [
    acceptWatchTimeLimit,
    cancelPendingRecovery,
    cancelPendingStartup,
    playAfterVideoElementRemountsRef,
    playbackStatusRef,
    resetVideoPlayer,
    startWatchTimeLimitTimer,
    startupDelayMs,
    getIsWatchTimeLimitReached,
  ]);

  return useMemo(
    () => ({
      acceptRuntimeGate,
      pause,
      play,
      resetAndRestart,
      restart,
      resume,
      stop,
    }),
    [acceptRuntimeGate, pause, play, resetAndRestart, restart, resume, stop],
  );
};

export default useVideoPlaybackActions;
